import { useEffect, useState } from "react";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import type { FirstStpReviewRuleDoc } from "./firstStpReviewRuleUtils";

/** Firestore document id for org-scoped first STP no-touch config (`…/no-touch-config/latest`). */
export const NO_TOUCH_CONFIG_DOC_ID = "latest";

/** Firestore document id for org-scoped second STP config (`…/no-touch-config/second_stp_review`). */
export const SECOND_STP_CONFIG_DOC_ID = "second_stp_review";

/** Firestore document id for org-scoped agentic config (`…/no-touch-config/agentic`). */
export const AGENTIC_CONFIG_DOC_ID = "agentic";

/** Which no-touch review stage a rules page is editing. */
export type StpVariant = "first" | "second" | "agentic";

/**
 * Document id and path resolver per review stage. Every stage is a sibling
 * document inside the same `api_config/{orgId}/no-touch-config` collection.
 */
const STP_VARIANT_DOCUMENTS: Record<
  StpVariant,
  { docId: string; path: (orgId: string) => string }
> = {
  first: {
    docId: NO_TOUCH_CONFIG_DOC_ID,
    path: FirestoreDocumentReference.noTouchConfig,
  },
  second: {
    docId: SECOND_STP_CONFIG_DOC_ID,
    path: FirestoreDocumentReference.secondStpConfig,
  },
  agentic: {
    docId: AGENTIC_CONFIG_DOC_ID,
    path: FirestoreDocumentReference.agenticConfig,
  },
};

/**
 * Returns the Firestore document id stored on the config payload for a review stage.
 *
 * @param variant - Review stage the config belongs to
 * @returns `latest`, `second_stp_review`, or `agentic`
 */
export function getStpConfigDocId(variant: StpVariant = "first"): string {
  return STP_VARIANT_DOCUMENTS[variant].docId;
}

/**
 * Returns the full Firestore document path for an org's no-touch config.
 *
 * @param orgId - Healthcare facility / organization id
 * @param variant - Review stage the config belongs to; defaults to first STP
 * @returns Document path under `no-touch-config` for that stage
 */
export function getStpConfigDocumentPath(
  orgId: string,
  variant: StpVariant = "first",
): string {
  return STP_VARIANT_DOCUMENTS[variant].path(orgId);
}

/**
 * @deprecated Use {@link getStpConfigDocumentPath} with {@link FirestoreService.setDocumentByPath}.
 */
export function getNoTouchConfigCollectionPath(orgId: string): string {
  return `api_config/${orgId}/no-touch-config`;
}

/**
 * Loads the STP review rules document from the org-scoped no-touch config path.
 *
 * @param orgId - Healthcare facility id; fetch is skipped until this is set
 * @param variant - Review stage to load; defaults to first STP
 * @returns Loaded document (only when it matches `orgId`), loading flag, fetch error, and `loadedOrgId`
 */
export function useFirstStpConfig(
  orgId: string,
  variant: StpVariant = "first",
) {
  const [data, setData] = useState<FirstStpReviewRuleDoc | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [loadedOrgId, setLoadedOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId.trim()) {
      setData(null);
      setError(null);
      setFetchLoading(true);
      setLoadedOrgId(null);
      return;
    }

    let cancelled = false;
    const requestedOrgId = orgId;

    const fetchConfig = async () => {
      setFetchLoading(true);
      setError(null);
      setData(null);
      setLoadedOrgId(null);
      try {
        const result =
          await FirestoreService.getDocumentOrNull<FirstStpReviewRuleDoc>(
            getStpConfigDocumentPath(requestedOrgId, variant),
          );
        if (!cancelled) {
          setData(result);
          setLoadedOrgId(requestedOrgId);
        }
      } catch (err) {
        const errorInstance =
          err instanceof Error ? err : new Error(String(err));
        if (!cancelled) {
          setError(errorInstance);
        }
      } finally {
        if (!cancelled) {
          setFetchLoading(false);
        }
      }
    };

    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [orgId, variant]);

  const isOrgDataReady = orgId.trim() !== "" && loadedOrgId === orgId;
  const loading =
    error == null &&
    (fetchLoading || (orgId.trim() !== "" && loadedOrgId !== orgId));
  const readyData = isOrgDataReady ? data : null;

  return { data: readyData, loading, error, loadedOrgId };
}
